import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { appeler } from '../../api/client.ts';
import { Sheet, Montant, Nombre } from '../../composants/ui/index.ts';
import { Erreur } from '../../composants/communs.tsx';
import type { LigneResultat, Resultats } from '../resultats/types.ts';

/**
 * Ce que chaque boutique a fait aujourd'hui.
 *
 * Le tableau de bord répondait « combien on a fait » et s'arrêtait là : une
 * pastille annonçait le NOMBRE de boutiques agrégées, ce qui ne dit rien de
 * ce que chacune a fait. Sur un outil dont la raison d'être est de tenir
 * plusieurs boutiques, la question d'ouverture d'écran est « laquelle marche,
 * et laquelle décroche » — et il fallait aller sur un autre écran pour elle.
 *
 * Les lignes sont classées par chiffre d'affaires DÉCROISSANT, et non par code
 * de boutique. Le classement est l'information : dans l'ordre des codes, il
 * faut comparer les nombres un à un pour le reconstituer.
 *
 * Le bloc n'apparaît qu'en CONSOLIDÉ. Sur une boutique désignée il ferait
 * doublon avec les chiffres du dessus.
 */
export function BlocParBoutique({ date }: { date: string }): ReactNode {
  /**
   * Les résultats du jour, par boutique.
   *
   * Même source que l'écran Résultats, bornée à une seule journée. L'appel
   * exige `rapport.ca.lire` : comparer les boutiques entre elles n'est pas
   * du ressort d'un vendeur, et l'appelant ne monte ce bloc que s'il l'a.
   */
  const { data, isPending, error } = useQuery({
    queryKey: ['resultats-du-jour', date],
    queryFn: () =>
      appeler<Resultats>(`/resultats?depuis=${date}&jusqu_a=${date}`),
  });

  if (error !== null) {
    return <Erreur erreur={error} />;
  }

  const lignes = [...(data?.lignes ?? [])].sort(
    (a, b) => b.chiffre_affaires - a.chiffre_affaires,
  );

  // Une seule boutique : il n'y a rien à comparer, le bloc ne s'affiche pas.
  if (!isPending && lignes.length < 2) {
    return null;
  }

  const meilleur = lignes[0]?.chiffre_affaires ?? 0;

  return (
    <Sheet
      titre="Par boutique, aujourd’hui"
      action={<Link to="/resultats">Comparer sur une période</Link>}
    >
      {isPending ? (
        <div className="squelette-pile">
          <div className="squelette" />
          <div className="squelette" />
          <div className="squelette" />
        </div>
      ) : (
        <ol className="classement">
          {lignes.map((ligne) => (
            <LigneBoutique key={ligne.boutique_id} ligne={ligne} meilleur={meilleur} />
          ))}
        </ol>
      )}
    </Sheet>
  );
}

function LigneBoutique({
  ligne,
  meilleur,
}: {
  ligne: LigneResultat;
  meilleur: number;
}): ReactNode {
  /**
   * Largeur de la barre : part du MEILLEUR, pas du total.
   *
   * Rapportée au total, une boutique sur cinq plafonnerait à 20 % et toutes
   * les barres seraient courtes et semblables. Rapportée au meilleur, la
   * première remplit la ligne et l'écart avec les autres se lit directement.
   */
  const part = meilleur <= 0 ? 0 : (ligne.chiffre_affaires / meilleur) * 100;

  /**
   * Un minimum d'1 % rend visible une boutique qui a vendu très peu — sinon
   * sa barre disparaît et on la croit à zéro.
   *
   * Mais quand PERSONNE n'a vendu, ce minimum affichait un moignon sur chaque
   * ligne, qui se lit comme une donnée. Une journée sans vente doit montrer
   * des barres vides, pas des traces.
   */
  const largeur = meilleur <= 0 ? 0 : Math.max(part, 1);

  // Ce qui mérite un mot à côté du chiffre. Une boutique sans rien à signaler
  // n'affiche rien : un « RAS » sur chaque ligne rendrait les vraies alertes
  // invisibles.
  const signaux: string[] = [];
  if (ligne.encours_echu > 0) {
    signaux.push('créances échues');
  }
  if (ligne.ecart_cumule !== 0) {
    signaux.push('écart de caisse');
  }
  if (ligne.contrepassations > 0) {
    signaux.push(
      `${String(ligne.contrepassations)} annulation${ligne.contrepassations > 1 ? 's' : ''}`,
    );
  }

  return (
    <li className="classement__ligne">
      <div className="classement__nom">
        <span className="mono">{ligne.code}</span> {ligne.nom}
      </div>

      <div className="classement__barre" aria-hidden="true">
        <span style={{ width: `${String(largeur)}%` }} />
      </div>

      <div className="classement__chiffre">
        <Montant valeur={ligne.chiffre_affaires} />
        <span className="classement__detail">
          <Nombre valeur={ligne.ventes} /> vente{ligne.ventes > 1 ? 's' : ''}
        </span>
      </div>

      {signaux.length > 0 && (
        <div className="classement__signal">
          <span className="alert-marqueur alert-gold" aria-hidden="true" />
          {signaux.join(' · ')}
        </div>
      )}
    </li>
  );
}
